import React, { useState, useRef, useEffect } from 'react';
import './OptimizedImage.sass';

const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  width, 
  height, 
  priority = false, // Para imágenes críticas (above the fold)
  sizes = "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw",
  quality = 80,
  placeholder = 'blur' // 'blur', 'empty', or custom component
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(priority); // Si es priority, cargar inmediatamente
  const imgRef = useRef();

  // Intersection Observer para lazy loading
  useEffect(() => {
    if (priority) return; // Skip lazy loading para imágenes priority

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px' // Empezar a cargar 50px antes de que sea visible
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  // Generar diferentes tamaños para responsive images
  const generateSrcSet = (baseSrc) => {
    if (!baseSrc) return '';
    
    const sizes = [320, 480, 640, 750, 828, 1080, 1200, 1920];
    return sizes.map(size => {
      // Si la imagen ya tiene parámetros, agregar más; si no, agregar los primeros
      const separator = baseSrc.includes('?') ? '&' : '?';
      return `${baseSrc}${separator}w=${size}&q=${quality} ${size}w`;
    }).join(', ');
  };

  const handleLoad = () => {
    setLoaded(true);
  };

  const handleError = () => {
    setError(true);
  };

  const containerStyle = {
    width: width || 'auto',
    height: height || 'auto',
    position: 'relative',
    overflow: 'hidden'
  };

  return (
    <div 
      ref={imgRef}
      className={`optimized-image ${className} ${loaded ? 'loaded' : ''}`}
      style={containerStyle}
    >
      {/* Placeholder mientras carga */}
      {!loaded && !error && (
        <div className="image-placeholder">
          {placeholder === 'blur' && (
            <div className="blur-placeholder">
              <div className="shimmer-effect"></div>
            </div>
          )}
          {placeholder === 'empty' && (
            <div className="empty-placeholder"></div>
          )}
        </div>
      )}

      {/* Imagen real */}
      {(inView || priority) && (
        <img
          src={src}
          srcSet={generateSrcSet(src)}
          sizes={sizes}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          onLoad={handleLoad}
          onError={handleError}
          className={`main-image ${loaded ? 'visible' : 'hidden'}`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* Error state */}
      {error && (
        <div className="image-error">
          <div className="error-content">
            <svg width="40" height="40" fill="currentColor" viewBox="0 0 24 24">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
            </svg>
            <span>Imagen no disponible</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;